import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { toastApiError } from "../../../api/apiError"
import { useAddArticleSupplier } from "../../../api/articlesApi"
import { SupplierSearchPicker } from "./SupplierSearchPicker"

const FIELD_CLASS = "border-grey4 bg-grey2 text-white"

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <label htmlFor={htmlFor} className="text-[11.5px] font-semibold text-grey5">
      {children}
    </label>
  )
}

export interface LinkSupplierToArticleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  articleId: string
  onLinked?: () => void
}

/**
 * Links an EXISTING supplier to an EXISTING article. Outer Dialog + inner
 * Body so all local state resets when DialogContent unmounts on close — same
 * remount pattern as LinkSubstanceDialog.tsx.
 */
export function LinkSupplierToArticleDialog({
  open,
  onOpenChange,
  articleId,
  onLinked,
}: LinkSupplierToArticleDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <LinkSupplierToArticleDialogBody
          articleId={articleId}
          onLinked={onLinked}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  )
}

function LinkSupplierToArticleDialogBody({
  articleId,
  onLinked,
  onOpenChange,
}: {
  articleId: string
  onLinked?: () => void
  onOpenChange: (open: boolean) => void
}) {
  const { translate } = useTranslation()
  const { mutate, isPending } = useAddArticleSupplier()

  const [pickedSupplierId, setPickedSupplierId] = useState<string | null>(null)
  const [supplierLabel, setSupplierLabel] = useState("")
  const [supplierArticleNumber, setSupplierArticleNumber] = useState("")

  const canSubmit = !!pickedSupplierId && !isPending

  function handleSubmit() {
    if (!pickedSupplierId) return
    mutate(
      {
        articleId,
        supplierId: pickedSupplierId,
        supplierArticleNumber: supplierArticleNumber.trim() || null,
      },
      {
        onSuccess: () => {
          onLinked?.()
          onOpenChange(false)
        },
        onError: (err) => toastApiError(err, translate, "MC_MD_SAVE_ERROR"),
      },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translate("MC_MD_LINK_SUPPLIER_TITLE")}</DialogTitle>
        <DialogDescription>
          {translate("MC_MD_LINK_SUPPLIER_DESCRIPTION")}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3 p-1">
        <div className="flex flex-col gap-1.5">
          <FieldLabel>{translate("MC_MD_PANEL_COL_SUPPLIER")}</FieldLabel>
          <SupplierSearchPicker
            triggerLabel={
              supplierLabel || translate("MC_MD_LINK_SUPPLIER_PICK")
            }
            onSelect={(id, label) => {
              if (id !== pickedSupplierId) setSupplierArticleNumber("")
              setPickedSupplierId(id)
              setSupplierLabel(label)
            }}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="link-supplier-article-number">
            {translate("MC_MD_LINK_SUPPLIER_ARTICLE_NUMBER")}
          </FieldLabel>
          <Input
            id="link-supplier-article-number"
            value={supplierArticleNumber}
            onChange={(e) => setSupplierArticleNumber(e.target.value)}
            className={FIELD_CLASS}
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {translate("MC_MD_EDIT_CANCEL")}
        </Button>
        <Button
          className="bg-yellow1 text-black hover:bg-yellow2"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {translate("MC_MD_LINK_SUPPLIER_SUBMIT")}
        </Button>
      </DialogFooter>
    </>
  )
}
